import '@widesk-ui/components/styles/Form.scss';

import AntdForm                     from 'antd/lib/form';
import { Rule }                     from 'antd/lib/form';
import ApiModel                     from '@widesk-core/models/ApiModel';
import Col                          from 'antd/lib/col';
import FormContext                  from '@widesk-ui/contexts/FormContext';
import React                        from 'react';
import Row                          from 'antd/lib/row';
import Spin                         from '@widesk-ui/components/Spin';
import View                         from '@widesk-ui/components/View';
import _get                         from 'lodash/get';
import _upperFirst                  from 'lodash/upperFirst';
import useDidMount                  from '@widesk-core/hooks/useDidMount';
import useModal                     from '@widesk-ui/hooks/useModal';
import useTheme                     from '@widesk-ui/hooks/useTheme';
import useWizardForm                from '@widesk-ui/hooks/useWizardForm';
import { FormItem }                 from '@widesk-ui/components/form/FormItem';
import { message }                  from '@widesk-ui/hooks/useMessage';
import Alert                        from '@widesk-ui/components/Alert';
import getTextAndTranslateFromError from '@widesk-core/tools/getTextAndTranslateFromError';
import Button                       from '../Button';
import useForceUpdate               from '@widesk-core/hooks/useForceUpdate';
import { ReactNode }                from 'react';
import { MarginValue }              from '@widesk-ui/components/View/viewTypes';
import useDrawer                    from '@widesk-ui/hooks/useDrawer';
import uniqueId                     from 'lodash/uniqueId';
import { SaveOutlined }             from '@ant-design/icons';
import Checkbox                     from '@widesk-ui/components/form/Checkbox';

export type FormFields = {
	[name: string]: FormField;
}

export type FormField = {
	forceLineBreak?: boolean; // Une nouvelle "Row" est ajoutée dans la grille à partir de ce champ
	hidden?: boolean | (() => boolean); // Si "true" le champ n'est pas monté
	disabled?: boolean;
	required?: boolean;
	span?: ColSize | (() => ColSize); // Permet de définir la largeur de la colonne dans la grille
	noFormItem?: boolean; // Les champs ne seront plus wrap dans un composant FormItem
	element: React.ReactNode;
	initialValue?: unknown;
	rules?: Rule[];
	colStyle?: React.CSSProperties;
} & ({
	noFormItem: true; // Quand "noFormItem" est true, on ne renseigne pas de label.
	label?: undefined;
	extra?: undefined;
} | {
	noFormItem?: false;
	label?: string;
	tooltip?: ReactNode;
	extra?: ReactNode;
});

export type FormProps<T extends FormFields> = {
	fields?: T | ((formTools: FormTools<T>) => T);
	noGrid?: boolean; // Permet de désactiver la grille ; les champs sont affichés côte à côtes
	onSubmit?: (values: { [Property in keyof T]: unknown }, tools: FormTools<T>) => Promise<any>;
	onSuccess?: (values: { [Property in keyof T]: unknown }, tools: FormTools<T>) => void;

	// Méthode appelée au chargement. Le contenu n'est pas monté tant que la promesse n'est pas résolue
	onDidMount?: (formTools: FormTools<T>) => Promise<void>;

	onChange?: (formTools: FormTools<T>) => void;

	successMessage?: string | false;
	children?: React.ReactNode;
	renderBeforeFields?: (formTools: FormTools<T>) => React.ReactNode;
	loading?: boolean;
	hideLoadingTip?: boolean; // Masque le texte "Chargement du formulaire" sous le spinner de chargement
	withButton?: boolean;
	/** Libellé du bouton de soumission ("Valider" par défaut) — pour qu'un formulaire de création dise
	 * "Ajouter" plutôt que de laisser l'utilisateur deviner ce qu'il valide. */
	submitLabel?: string;
	continueAfterSubmit?: boolean | {
		defaultEnabled?: true;
		onContinue?: (formTools: FormTools<T>) => void;
	},
	updateOnChange?: boolean; // Déclenche un render complet du formulaire quand un Field est modifié
	gap?: MarginValue | number;
	style?: React.CSSProperties;
}

export type FormTools<T extends FormFields> = {
	getValues: () => Record<keyof T, string>;
	getFieldValue: (name: keyof T) => string;
	setFieldValue: (name: keyof T | string[], value: unknown) => void;
	setFieldsValue: (values: Partial<Record<keyof T, unknown>>) => void;
	setSubmitting: (value: boolean) => void;
	getDisabled: () => boolean;
	setDisabled: (value: boolean) => void;
	resetFields: (name?: (keyof T | string[])[]) => void;
	submit: () => void;
	validateFields: () => void;
}

export default function Form<T extends FormFields>(props: FormProps<T>) {
	const {
		renderBeforeFields,
		continueAfterSubmit,
		children,
		noGrid,
		onDidMount,
		onSubmit,
		updateOnChange,
		successMessage,
		style,
		withButton,
	} = props;

	const [form] = AntdForm.useForm();
	const { wizardFormStore } = useWizardForm();
	const { modalStore } = useModal();
	const forceUpdate = useForceUpdate();
	const theme = useTheme();
	const drawerContext = useDrawer();

	const [loading, setLoading] = React.useState(!!onDidMount);
	const [errors, setErrors] = React.useState<string[]>([]);
	const [continued, setContinued] = React.useState<boolean>(
		(typeof continueAfterSubmit === 'object' && continueAfterSubmit.defaultEnabled) || false
	);
	const formId = React.useMemo(() => uniqueId(), []);

	const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

	const onChange = (props.onChange || updateOnChange) ? (t: FormTools<T>) => {
		props.onChange?.(t);

		if (updateOnChange) {
			forceUpdate();
		}
	} : undefined;

	const tools: FormTools<T> = {
		getValues: () => form.getFieldsValue(),
		getFieldValue: (name) => form.getFieldValue(name),
		setFieldValue: (name, value) => form.setFieldValue(name, value),
		setFieldsValue: (values) => form.setFieldsValue(values),
		setSubmitting: (value: boolean) => modalStore?.setIsSubmitting(value),
		getDisabled: () => !!modalStore?.isDisabled,
		setDisabled: (value: boolean) => modalStore?.setIsDisabled(value),
		resetFields: () => form.resetFields(),
		submit: form.submit,
		validateFields: () => form.validateFields(),
	};

	const fields = typeof props.fields === 'function'
		? props.fields(tools)
		: props.fields || {} as T;

	const columnNamesByRow = Object.entries(fields).reduce<string[][]>((acc, [name, field]) => {
		if (field.forceLineBreak) {
			acc.push([]);
		}

		acc[acc.length - 1]?.push(name);

		return acc;
	}, [[]]);

	const onFinish = async (values: Record<keyof T, unknown>) => {
		try {
			tools.setSubmitting(true);
			setIsSubmitting(true);

			const result = onSubmit ? await onSubmit(values, tools) : undefined;

			// On ne passe pas à la suite si la méthode "onSubmit" retourne "false".
			if (result === false) {
				return false;
			}

			if (result instanceof ApiModel && successMessage !== false) {
				const currentSuccessMessage = (
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					successMessage || _upperFirst(`L'élément ${result.localize()} a bien été enregistré.`)
				);
				message.success(currentSuccessMessage);
			}

			props.onSuccess?.(values, tools);

			// Si le formulaire est dans une modale ET qu'il n'est pas dans un wizard
			if (modalStore && !wizardFormStore) {
				modalStore.setIsOpen(false); // On ferme la modale
			}

			setErrors([]);

			// On retourne les values (elles sont utilisées dans le WizardForm).
			return values;
		} catch (e) {
			const violations: any[] = _get(e, 'connectorError.response.data.violations', []);
			const fields = form.getFieldsValue();

			// Permet d'afficher le message d'erreur de l'api sous le champ qui correspond
			form.setFields(
				violations
					.map(v => {
						const vN = v.propertyPath;
						const name = [vN, `${vN}Urn`, `${vN}Urns`].find(n => Object.prototype.hasOwnProperty.call(fields, n));
						return { errors: [v.message], name };
					})
					.filter(v => v.name),
			);

			const textFromError = getTextAndTranslateFromError(e);
			if (textFromError) {
				setErrors([textFromError]);
			}

			throw e;
		} finally {
			tools.setSubmitting(false);
			setIsSubmitting(false);

			if (continueAfterSubmit) {
				tools.resetFields();
				if (typeof continueAfterSubmit === 'object') {
					continueAfterSubmit.onContinue?.(tools);
				}
			}
			if (drawerContext?.closeDrawer && !continued) {
				drawerContext.closeDrawer();
			}
		}
	};

	useDidMount(async () => {
		// On assigne la méthode submit à l'action "submit" de la modale
		modalStore?.setSubmitCallback(() => form.submit());

		if (onDidMount) {
			try {
				await onDidMount(tools);

				setLoading(false);

				if (onChange) {
					onChange(tools);
				}
			} catch (err) {
				console.error(err);
				throw err;
			}
		} else if (onChange) {
			onChange(tools);
		}

		// Si on est dans un wizardForm, on utilise les valeurs enregistrées dans la store wizard
		if (wizardFormStore && wizardFormStore.hasValues) {
			tools.setFieldsValue(wizardFormStore.values);
		}
	});

	useDidMount(() => {
		// Méthode qui va être utilisée par le WizardForm pour soumettre le formulaire
		wizardFormStore?.setDefaultSubmit(async () => {
			await form.validateFields();
			return onFinish(form.getFieldsValue());
		});
		// Méthode qui va être utilisée par le WizardForm pour récupérer les values en tant que paramètre des FormStep.onSubmit
		wizardFormStore?.setCurrentValues(() => form.getFieldsValue());

		return () => {
			wizardFormStore?.setDefaultSubmit(undefined);
			wizardFormStore?.setCurrentValues(undefined);
		};
	});

	const CustomRow = noGrid ? NoWrapper : Row;
	const CustomCol = noGrid ? NoWrapper : Col;
	const gap = props.gap || (drawerContext ? 'sm' : 'lg');
	const customMargin = !!gap && typeof gap === 'string'
		? theme[`margin${gap.toUpperCase()}` as keyof typeof theme] as number
		: gap;

	React.useEffect(() => {
		// `withButton === false` : aucun pied posé dans le tiroir. Pour un panneau qui enregistre au fil
		// de l'eau, un "Valider" ne correspond à aucune action restante et laisse croire que le reste
		// n'est pas parti.
		if (props.withButton === false) return;

		if (drawerContext && drawerContext.setFooter && !drawerContext.footer) {
			const footer = (
				<View centerV gapV="xs" row wrap>
					{!!continueAfterSubmit && (
						<Checkbox
							onChange={(e: any) => setContinued(e.target.checked)}
							value={continued}
						>
							Enregistrer et continuer
						</Checkbox>
					)}
					<Button style={{ flex: 1 }} icon={<SaveOutlined />} form={formId} type="primary" htmlType="submit">{props.submitLabel || 'Valider'}</Button>
				</View>
			);
			drawerContext.setFooter(footer);
		}
	});

	return (
		<FormContext.Provider value={{ fields, tools }}>
			<View gap={gap}>
				{!!errors.length && <Alert type="error" showIcon message={errors} />}

				<AntdForm.Provider onFormChange={() => {
					if (onChange) {
						onChange(tools);
					}
				}}>
					<AntdForm
						style={style}
						className="widesk-form"
						labelCol={{ span: 24 }}
						form={form}
						onFinish={onFinish}
						id={modalStore?.props.okButtonProps?.form || formId}
					>
						{(loading || isSubmitting || props.loading) ? (
							<Spin paddingV="lg" size="large" tip={props.hideLoadingTip ? undefined : 'Chargement du formulaire'} />
						) : (
							<View gap={gap}>
								{renderBeforeFields?.(tools)}

								{columnNamesByRow.map((names, rowIdx) => {
									/** Permet de déterminer si tous les Cols de la Row sont "hidden" */
									const allHidden = !names.some(name => {
										const field = fields[name]!;
										if (typeof field.hidden === 'function') {
											return !field.hidden();
										}
										return !field.hidden;
									});

									if (allHidden) {
										return null;
									}

									return (
										<CustomRow key={rowIdx} gutter={customMargin ? [customMargin, customMargin] : [theme.marginLG, theme.marginLG]}>
											{names.map((name, colIdx) => {
												const key = colIdx + name;
												const field = fields[name]!;
												const span = typeof field.span === 'function' ? field.span() : (field.span || 12);

												if (typeof field.hidden === 'function' && field.hidden()) {
													return null;
												} else if (field.hidden) {
													return null;
												}

												return (
													<CustomCol key={key} span={span} style={field.colStyle}>
														{field.noFormItem ? field.element : (
															<FormItem
																tooltip={field.tooltip}
																extra={field.extra}
																required={field.required}
																label={field.label}
																name={name}
																initialValue={field.initialValue}
																rules={field.rules}
															>
																{field.element as any}
															</FormItem>
														)}
													</CustomCol>
												);
											})}
										</CustomRow>
									);
								})}

								{children}

								{!drawerContext?.footer && (!!continueAfterSubmit || props.withButton) && (
									<View centerV gapV="xs" row wrap>
										{!!continueAfterSubmit && (
											<Checkbox
												onChange={(e: any) => setContinued(e.target.checked)}
												value={continued}
											>
												Enregistrer et continuer
											</Checkbox>
										)}
										{withButton && (
											<Button style={{ flex: 1 }} type="primary" htmlType="submit">
												{props.submitLabel || 'Valider'}
											</Button>
										)}
									</View>
								)}
							</View>
						)}
					</AntdForm>
				</AntdForm.Provider>
			</View>
		</FormContext.Provider>
	);
}

const NoWrapper = ({ children }: { children: React.ReactNode }) => <>{children}</>;

Form.Item = FormItem;
Form.List = AntdForm.List;
Form.ErrorList = AntdForm.ErrorList;
Form.RawItem = AntdForm.Item;
